import { useEffect, useRef } from "react";

const UDID_CARD_IMG = "/images/udid-card.jpeg";

const benefits = [
  { icon: "🎓", label: "Disability-related scholarships and educational support" },
  { icon: "🏛️", label: "Government welfare schemes" },
  { icon: "🚌", label: "Travel concessions in eligible circumstances" },
  { icon: "🦾", label: "Assistive devices and rehabilitation services" },
  { icon: "💰", label: "Disability pensions and financial assistance schemes" },
  { icon: "📋", label: "Reservation benefits in education and employment where applicable" },
  { icon: "🏥", label: "Certain health and insurance-related disability schemes" },
  { icon: "🗺️", label: "State-specific disability welfare programs" },
];

const misconceptions = [
  {
    myth: "The UDID card is only useful for adults.",
    fact: "Many children benefit from having documentation completed early, particularly when educational supports, government schemes, or future services may require disability verification.",
  },
  {
    myth: "The card automatically provides benefits.",
    fact: "The UDID card is best understood as a gateway document. It helps establish eligibility and identity, but separate applications may still be required for individual schemes and services.",
  },
];

const references = [
  "Department of Empowerment of Persons with Disabilities (DEPwD), Government of India – Unique Disability ID (UDID) Project.",
  "National Government Services Portal – UDID Card Services and Application Information.",
  "National Institute for the Empowerment of Persons with Intellectual Disabilities (NIEPID) – UDID Information.",
];

export default function UDIDBlogPost() {
  const animRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.08 }
    );
    animRefs.current.forEach((r) => r && observer.observe(r));
    return () => observer.disconnect();
  }, []);

  const ref = (el) => {
    if (el && !animRefs.current.includes(el)) animRefs.current.push(el);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Inter:wght@300;400;500;600&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        body {
          background: #F6F8FF;
          color: #252525;
          font-family: 'Inter', sans-serif;
          line-height: 1.8;
        }

        /* ── Top band ── */
        .top-band {
          background: #1A3A6B;
          padding: 0.85rem clamp(1.5rem, 8vw, 9rem);
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .top-band .tag {
          font-size: 0.68rem;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #A8C4F0;
        }
        .top-band .dot { color: #4A7EC4; font-size: 0.5rem; }
        .top-band .org { font-size: 0.75rem; color: rgba(255,255,255,0.55); }

        /* ── Hero ── */
        .hero {
          background: linear-gradient(135deg, #1A3A6B 0%, #0F2448 60%, #1A3A6B 100%);
          padding: clamp(3rem, 7vw, 5rem) clamp(1.5rem, 8vw, 9rem);
          position: relative;
          overflow: hidden;
        }

        .hero::before {
          content: 'UDID';
          position: absolute;
          right: clamp(2rem, 8vw, 8rem);
          top: 50%;
          transform: translateY(-50%);
          font-family: 'Playfair Display', serif;
          font-size: clamp(6rem, 14vw, 14rem);
          font-weight: 700;
          color: rgba(255,255,255,0.04);
          letter-spacing: 0.05em;
          pointer-events: none;
          user-select: none;
        }

        .hero-inner { max-width: 660px; position: relative; z-index: 1; }

        .hero-eyebrow {
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: #7AABF0;
          margin-bottom: 1.1rem;
        }

        .hero h1 {
          font-family: 'Playfair Display', serif;
          font-size: clamp(1.8rem, 4vw, 3rem);
          font-weight: 700;
          color: #fff;
          line-height: 1.2;
          margin-bottom: 1.5rem;
        }

        /* ── ID card visual ── */
        .id-card-visual {
          display: inline-flex;
          align-items: center;
          gap: 1.2rem;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.14);
          border-radius: 10px;
          padding: 1rem 1.6rem;
          margin-top: 0.5rem;
        }

        .id-card-icon {
          width: 44px;
          height: 30px;
          background: linear-gradient(135deg, #4A90E2, #1A3A6B);
          border-radius: 5px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1rem;
          flex-shrink: 0;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        }

        .id-card-text { font-size: 0.82rem; color: rgba(255,255,255,0.65); }
        .id-card-text strong { display: block; color: #fff; font-size: 0.95rem; }

        /* ── Wave ── */
        .wave { display: block; width: 100%; line-height: 0; background: #F6F8FF; }
        .wave svg { display: block; width: 100%; }

        /* ── Layout ── */
        .article-layout {
          display: grid;
          grid-template-columns: 1fr min(700px, 90%) 1fr;
        }
        .article-layout > * { grid-column: 2; }
        .full-bleed { grid-column: 1 / -1 !important; }

        /* ── Opening questions ── */
        .opening-questions {
          padding: 3.5rem 0 1rem;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.65s ease, transform 0.65s ease;
        }
        .opening-questions.visible { opacity: 1; transform: none; }

        .q-stack { margin-bottom: 2rem; }

        .q-stack .q-item {
          font-family: 'Playfair Display', serif;
          font-style: italic;
          font-size: 1.1rem;
          color: #1A3A6B;
          padding: 0.35rem 0 0.35rem 1.2rem;
          border-left: 2px solid #B8CEF0;
          margin-bottom: 0.5rem;
          opacity: 0.85;
          transition: opacity 0.2s;
        }

        .opening-questions p {
          font-size: 1.06rem;
          color: #383838;
          margin-bottom: 1.3rem;
          line-height: 1.85;
        }

        /* ── RHCA callout ── */
        .rhca-callout {
          background: #EBF2FF;
          border-radius: 6px;
          border-left: 3px solid #4A7EC4;
          padding: 1.4rem 2rem;
          margin: 1.5rem 0;
          opacity: 0;
          transform: translateX(-16px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }
        .rhca-callout.visible { opacity: 1; transform: none; }

        .rhca-callout .callout-label {
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #4A7EC4;
          margin-bottom: 0.5rem;
        }

        .rhca-callout p {
          font-family: 'Playfair Display', serif;
          font-style: italic;
          font-size: 1.15rem;
          color: #1A3A6B;
          line-height: 1.6;
        }

        /* ── Blue divider ── */
        .blue-divider {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.8rem 0;
          opacity: 0;
          transform: scaleX(0.5);
          transform-origin: left;
          transition: opacity 0.8s ease, transform 0.8s ease;
        }
        .blue-divider.visible { opacity: 1; transform: scaleX(1); }
        .blue-divider::before, .blue-divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: #4A7EC4;
          opacity: 0.4;
        }
        .div-icon { color: #4A7EC4; font-size: 0.9rem; letter-spacing: 0.4em; }

        /* ── Body section ── */
        .body-section {
          padding: 0.3rem 0 1.4rem;
          opacity: 0;
          transform: translateY(22px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }
        .body-section.visible { opacity: 1; transform: none; }

        .section-heading {
          font-family: 'Playfair Display', serif;
          font-size: 1.45rem;
          font-weight: 600;
          color: #1A3A6B;
          margin: 0.3rem 0 1rem;
          line-height: 1.3;
        }

        .body-section p {
          font-size: 1.04rem;
          color: #383838;
          margin-bottom: 1.3rem;
          line-height: 1.85;
        }

        /* ── Definition box ── */
        .definition-box {
          background: #fff;
          border: 1px solid #C8D9F5;
          border-radius: 8px;
          padding: 2rem 2.2rem;
          margin: 1.5rem 0;
          position: relative;
          opacity: 0;
          transform: translateY(16px);
          transition: opacity 0.65s ease, transform 0.65s ease;
        }
        .definition-box.visible { opacity: 1; transform: none; }

        .definition-box .def-label {
          position: absolute;
          top: -0.6rem;
          left: 1.5rem;
          background: #1A3A6B;
          color: #fff;
          font-size: 0.68rem;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          padding: 3px 10px;
          border-radius: 3px;
        }

        .definition-box p {
          font-size: 1.04rem;
          color: #383838;
          line-height: 1.8;
          margin-bottom: 0.8rem;
        }
        .definition-box p:last-child { margin-bottom: 0; }

        /* ── Benefits grid ── */
        .benefits-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.85rem;
          margin: 1.5rem 0 2rem;
        }

        .benefit-card {
          background: #fff;
          border: 1px solid #D0E1FA;
          border-radius: 6px;
          padding: 1.1rem 1.3rem;
          display: flex;
          align-items: flex-start;
          gap: 0.9rem;
          opacity: 0;
          transform: translateY(14px);
          transition: opacity 0.5s ease, transform 0.5s ease;
        }

        .benefit-card.visible { opacity: 1; transform: none; }

        .benefit-icon {
          font-size: 1.3rem;
          line-height: 1;
          flex-shrink: 0;
          margin-top: 0.1rem;
        }

        .benefit-text {
          font-size: 0.92rem;
          color: #3a3a3a;
          line-height: 1.55;
        }

        .benefits-note {
          font-size: 0.85rem;
          color: #777;
          font-style: italic;
          margin: 0.5rem 0 1.5rem;
          padding: 0.8rem 1.2rem;
          background: #F0F5FF;
          border-radius: 4px;
          border-left: 2px solid #B0C8F0;
        }

        /* ── States section ── */
        .states-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.2rem;
          margin: 1.5rem 0;
        }

        .state-card {
          border-radius: 8px;
          padding: 1.6rem;
          opacity: 0;
          transform: translateY(16px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }
        .state-card.visible { opacity: 1; transform: none; }

        .state-card.tn { background: #FFF4E6; border: 1px solid #F0D4A8; }
        .state-card.ka { background: #EDF5FF; border: 1px solid #B8D4F5; }

        .state-flag {
          font-size: 1.8rem;
          margin-bottom: 0.7rem;
          display: block;
        }

        .state-card h3 {
          font-family: 'Playfair Display', serif;
          font-size: 1.1rem;
          font-weight: 600;
          color: #252525;
          margin-bottom: 0.6rem;
        }

        .state-card p {
          font-size: 0.92rem;
          color: #555;
          line-height: 1.65;
        }

        /* ── Misconceptions ── */
        .misconception-block {
          margin: 1.5rem 0;
          opacity: 0;
          transform: translateY(18px);
          transition: opacity 0.65s ease, transform 0.65s ease;
        }
        .misconception-block.visible { opacity: 1; transform: none; }

        .mc-item {
          margin-bottom: 1.2rem;
          border-radius: 6px;
          overflow: hidden;
          border: 1px solid #E0E8F8;
        }

        .mc-myth {
          background: #F5E8E8;
          padding: 0.9rem 1.3rem;
          display: flex;
          align-items: baseline;
          gap: 0.8rem;
        }

        .mc-myth-label {
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #B84444;
          flex-shrink: 0;
          background: #B84444;
          color: #fff;
          padding: 2px 8px;
          border-radius: 3px;
        }

        .mc-myth-text {
          font-size: 0.95rem;
          color: #6B2020;
          font-style: italic;
        }

        .mc-fact {
          background: #EBF5EC;
          padding: 0.9rem 1.3rem;
          display: flex;
          align-items: baseline;
          gap: 0.8rem;
        }

        .mc-fact-label {
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          background: #2E7D32;
          color: #fff;
          padding: 2px 8px;
          border-radius: 3px;
          flex-shrink: 0;
        }

        .mc-fact-text {
          font-size: 0.95rem;
          color: #1B4D1E;
          line-height: 1.6;
        }

        /* ── Closing card ── */
        .closing-card {
          background: #1A3A6B;
          color: #fff;
          border-radius: 8px;
          padding: 2.8rem 3rem;
          margin: 2.5rem 0;
          opacity: 0;
          transform: translateY(22px);
          transition: opacity 0.7s ease, transform 0.7s ease;
        }
        .closing-card.visible { opacity: 1; transform: none; }

        .closing-card h2 {
          font-family: 'Playfair Display', serif;
          font-size: 1.55rem;
          margin-bottom: 1rem;
          color: #fff;
        }

        .closing-card p {
          font-size: 1.02rem;
          color: rgba(255,255,255,0.82);
          line-height: 1.8;
          margin-bottom: 0.9rem;
        }
        .closing-card p:last-child { margin-bottom: 0; }

        .closing-card .highlight { color: #7AABF0; font-weight: 600; font-style: normal; }

        /* ── References ── */
        .references-block {
          margin: 1rem 0 2.5rem;
          opacity: 0;
          transform: translateY(14px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }
        .references-block.visible { opacity: 1; transform: none; }

        .ref-heading {
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #888;
          margin-bottom: 0.8rem;
        }

        .ref-list { list-style: none; }

        .ref-list li {
          font-size: 0.83rem;
          color: #777;
          padding: 0.4rem 0 0.4rem 1.2rem;
          border-left: 2px solid #C8D9F5;
          margin-bottom: 0.5rem;
          line-height: 1.55;
        }

        /* ── Author ── */
        .author-line {
          text-align: right;
          font-family: 'Playfair Display', serif;
          font-style: italic;
          font-size: 1.05rem;
          color: #1A3A6B;
          padding: 0.5rem 0 3rem;
        }

        /* ── Footer ── */
        .blog-footer {
          grid-column: 1 / -1;
          background: #0D1F3C;
          color: rgba(255,255,255,0.38);
          text-align: center;
          padding: 1.6rem;
          font-size: 0.75rem;
          letter-spacing: 0.06em;
        }

        @media (max-width: 640px) {
          .benefits-grid { grid-template-columns: 1fr; }
          .states-grid { grid-template-columns: 1fr; }
          .closing-card { padding: 2rem 1.5rem; }
          .hero::before { display: none; }
        }
      `}</style>

      {/* ── Top band ── */}
      <div className="top-band">
        <span className="tag">Rights &amp; Resources</span>
        <span className="dot">●</span>
        <span className="org">Ray of Hope Center for Autism</span>
      </div>

      {/* ── Hero ── */}
      <header className="hero">
        <div className="hero-inner">
          <p className="hero-eyebrow">Parent Guide · Government Benefits</p>
          <h1>The Card Many Families Don't Know They Need: Understanding the UDID Card</h1>
          <div className="id-card-visual">
            <div className="id-card-icon">🪪</div>
            <div className="id-card-text">
              <strong>Unique Disability ID (UDID)</strong>
              Government of India · Ministry of Social Justice &amp; Empowerment
            </div>
          </div>
        </div>
      </header>

      {/* Wave */}
      <div className="wave">
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none" height="60">
          <path d="M0,10 C400,70 1040,0 1440,35 L1440,60 L0,60 Z" fill="#F6F8FF" />
        </svg>
      </div>

      <main className="article-layout">

        <div style={{ textAlign: "center", marginBottom: "2.5rem" }} ref={ref}>
          <img 
            src={UDID_CARD_IMG} 
            alt="Unique Disability ID (UDID) Card Guide for Autism & Special Needs" 
            style={{ width: "100%", maxWidth: "800px", borderRadius: "12px", boxShadow: "0 10px 30px rgba(0,0,0,0.08)" }} 
          />
        </div>

        {/* Opening */}
        <div className="opening-questions" ref={ref}>
          <p>A parent receives a diagnosis for their child. The first thoughts are rarely about paperwork.</p>
          <p>They are about questions.</p>
          <div className="q-stack">
            {["What does this mean for my child?", "What support will they need?", "Will they be able to go to school?", "Will they be independent?", "What does the future look like?"].map((q, i) => (
              <div className="q-item" key={i}>{q}</div>
            ))}
          </div>
          <p>
            In the middle of therapy appointments, school meetings, assessments, and everyday life, one important step is often overlooked — the <strong>Unique Disability ID (UDID) Card</strong>.
          </p>
          <p>
            Many families only hear about the UDID card when applying for a government scheme, scholarship, travel concession, or disability-related benefit. By then, they often find themselves rushing through paperwork and navigating a process they wish they had started earlier.
          </p>
        </div>

        <div className="rhca-callout" ref={ref}>
          <div className="callout-label">At Ray of Hope, we often hear</div>
          <p>"Do we really need a UDID card?"</p>
          <p style={{ fontFamily: "'Inter', sans-serif", fontStyle: "normal", fontSize: "0.95rem", color: "#2a4a7a", marginTop: "0.6rem", lineHeight: "1.7" }}>
            For most children with a recognized disability, the answer is <strong>yes.</strong>
          </p>
        </div>

        {/* Divider */}
        <div className="blue-divider" ref={ref}><span className="div-icon">✦ ✦ ✦</span></div>

        {/* What is UDID */}
        <div className="body-section" ref={ref}>
          <h2 className="section-heading">What Is a UDID Card?</h2>
          <p>
            The Unique Disability ID (UDID) Card is a Government of India initiative launched by the Department of Empowerment of Persons with Disabilities (DEPwD), Ministry of Social Justice and Empowerment. The goal is to create a single national identity for persons with disabilities while simplifying access to disability-related services and benefits.
          </p>
        </div>

        <div className="definition-box" ref={ref}>
          <div className="def-label">In Simple Terms</div>
          <p>
            Think of it as a <strong>nationally recognized disability identity card</strong> that combines disability-related information into one standardized system — usable across India.
          </p>
          <p>
            Rather than relying on multiple certificates issued by different authorities, the UDID system aims to provide a uniform and transparent mechanism for accessing services and benefits.
          </p>
        </div>

        {/* Divider */}
        <div className="blue-divider" ref={ref}><span className="div-icon">✦ ✦ ✦</span></div>

        {/* Why important */}
        <div className="body-section" ref={ref}>
          <h2 className="section-heading">Why Is It Important for Families?</h2>
          <p>
            Many parents assume the card is only necessary if they plan to apply for government assistance immediately. In reality, the UDID card often becomes important at different stages of a child's life — and having it ready makes every process smoother because disability information has already been verified through the UDID system.
          </p>
          <p>
            For autism, intellectual disability, cerebral palsy, hearing impairment, visual impairment, multiple disabilities, and other recognized disabilities, obtaining a UDID card can be an important step in ensuring access to support both now and in the future.
          </p>
        </div>

        {/* Benefits grid */}
        <div className="body-section" ref={ref}>
          <h2 className="section-heading">What Benefits Can a UDID Card Help Access?</h2>
        </div>

        <div className="benefits-grid">
          {benefits.map((b, i) => (
            <div
              className="benefit-card"
              key={i}
              ref={ref}
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <span className="benefit-icon">{b.icon}</span>
              <span className="benefit-text">{b.label}</span>
            </div>
          ))}
        </div>

        <p className="benefits-note" ref={ref}>
          The card itself does not automatically guarantee benefits. Rather, it serves as a recognized disability identification document that can be used when applying for eligible schemes and services.
        </p>

        {/* Divider */}
        <div className="blue-divider" ref={ref}><span className="div-icon">✦ ✦ ✦</span></div>

        {/* States */}
        <div className="body-section" ref={ref}>
          <h2 className="section-heading">What About Families in Tamil Nadu and Karnataka?</h2>
          <p>
            This is particularly relevant for the families we serve at Ray of Hope. Both states have active disability welfare departments and various schemes that may require UDID-linked documentation.
          </p>
        </div>

        <div className="states-grid">
          <div className="state-card tn" ref={ref}>
            <span className="state-flag">🏛️</span>
            <h3>Tamil Nadu</h3>
            <p>
              Families may encounter UDID requirements when applying for educational assistance programs, disability pensions, maintenance allowances, assistive devices, skill development initiatives, scholarship programs, and transport-related concessions.
            </p>
          </div>
          <div className="state-card ka" ref={ref}>
            <span className="state-flag">🏛️</span>
            <h3>Karnataka</h3>
            <p>
              Similar welfare benefits are available through the state disability welfare department. Having a UDID card in place places families in a stronger position when these opportunities arise.
            </p>
          </div>
        </div>

        <div className="body-section" ref={ref}>
          <p style={{ fontSize: "0.92rem", color: "#666", fontStyle: "italic" }}>
            Because policies and eligibility criteria can change over time, families should always verify current scheme requirements with their respective state disability welfare departments.
          </p>
        </div>

        {/* Divider */}
        <div className="blue-divider" ref={ref}><span className="div-icon">✦ ✦ ✦</span></div>

        {/* Misconceptions */}
        <div className="body-section" ref={ref}>
          <h2 className="section-heading">A Few Common Misconceptions</h2>
        </div>

        <div className="misconception-block" ref={ref}>
          {misconceptions.map((mc, i) => (
            <div className="mc-item" key={i}>
              <div className="mc-myth">
                <span className="mc-myth-label">Myth</span>
                <span className="mc-myth-text">{mc.myth}</span>
              </div>
              <div className="mc-fact">
                <span className="mc-fact-label">Fact</span>
                <span className="mc-fact-text">{mc.fact}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div className="blue-divider" ref={ref}><span className="div-icon">✦ ✦ ✦</span></div>

        {/* Closing */}
        <div className="closing-card" ref={ref}>
          <h2>Looking Ahead</h2>
          <p>
            As parents, it is natural to focus on today's challenges — therapy sessions, school routines, communication goals, and daily victories. Yet some of the most important decisions we make are the ones that <span className="highlight">prepare for tomorrow.</span>
          </p>
          <p>
            The UDID card may seem like just another document. In reality, it can become an important key that helps unlock services, supports, accommodations, and opportunities throughout a child's life.
          </p>
          <p>
            At Ray of Hope, we encourage families to learn about the UDID process early, ask questions, and stay informed about the resources available to them. Because meaningful support is not only about the skills a child learns today — it is also about ensuring they have access to the <span className="highlight">opportunities they may need in the years ahead.</span>
          </p>
        </div>

        {/* References */}
        <div className="references-block" ref={ref}>
          <p className="ref-heading">References</p>
          <ol className="ref-list">
            {references.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ol>
        </div>

        {/* Author */}
        <p className="author-line">— Keziah George</p>

        <footer className="blog-footer">
          © 2026 Ray of Hope Center for Autism · Asian Christian Academy · All rights reserved
        </footer>

      </main>
    </>
  );
}
